// The wilds' voice: tiny synthesized cues keyed off threat-message kinds.
// No samples, no assets — a couple of oscillators and a noise burst per
// cue, quiet and short. The rule from the design side: audio is
// TELEGRAPHY, part of the same warning ladder as prose and posture, so a
// cue fires only when the server has said something (there is no
// client-side creature "AI sound"), and it carries no information the
// words did not.

export class WildsAudio {
  private ctx: AudioContext | undefined;

  private ensure(): AudioContext | undefined {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return undefined;
      }
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  /** Play the cue for a threat kind. Unknown kinds are silent. */
  cue(kind: string): void {
    switch (kind) {
      case "noticed":
        this.shriek(0.16, 1400, 700);
        return;
      case "stalked":
        this.shriek(0.22, 1100, 500);
        return;
      case "challenge":
        this.hum();
        return;
      case "struck":
      case "covered":
        this.thud(0.5);
        return;
      case "braced":
        this.thud(0.28);
        return;
      case "felled":
        this.thud(0.7);
        return;
      default:
        return; // sign, evaded, landed, drove: words are enough
    }
  }

  /** A thin falling cry — the skirl's register. */
  private shriek(seconds: number, fromHz: number, toHz: number): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(fromHz, now);
    osc.frequency.exponentialRampToValueAtTime(toHz, now + seconds);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = (fromHz + toHz) / 2;
    filter.Q.value = 4;
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + seconds + 0.05);
  }

  /** A low electrical presence — the warden's register. */
  private hum(): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = 84;
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 168;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.08);
    gain.gain.setValueAtTime(0.035, now + 0.7);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc2.start(now);
    osc.stop(now + 1.05);
    osc2.stop(now + 1.05);
  }

  /** Impact: a filtered noise burst, weight by loudness. */
  private thud(strength: number): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const now = ctx.currentTime;
    const length = Math.floor(ctx.sampleRate * 0.12);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 300 + strength * 300;
    const gain = ctx.createGain();
    gain.gain.value = 0.09 * strength;
    src.connect(filter).connect(gain).connect(ctx.destination);
    src.start(now);
  }
}
